import React from "react";
import { useViewMode } from "@/lib/ViewModeContext";
import { Lightning, Flask } from "@phosphor-icons/react";

/**
 * Live/Test mode indicator + toggle for the sidebar / mobile top-bar.
 * Click to switch; persists the choice per-user via ViewModeContext.
 *
 * Two visual variants:
 *   • compact=false (default) — full-width segmented control for the sidebar
 *   • compact=true            — minimal pill for the mobile top-bar
 */
export default function ViewModeBadge({ compact = false, testId = "view-mode" }) {
  const ctx = useViewMode();
  if (!ctx) return null;
  const { viewMode, setViewMode } = ctx;

  if (compact) {
    const isTest = viewMode === "test";
    return (
      <button
        type="button"
        onClick={() => setViewMode(isTest ? "live" : "test")}
        data-testid={`${testId}-compact`}
        aria-label={`Switch to ${isTest ? "Live" : "Test"} mode`}
        title={`${isTest ? "Test" : "Live"} mode — click to switch`}
        className={`inline-flex items-center gap-1 px-2 h-7 rounded-md text-[10px] uppercase tracking-widest font-black border transition-colors ${
          isTest
            ? "bg-yellow-400 text-yellow-950 border-yellow-500"
            : "bg-emerald-500/20 text-emerald-300 border-emerald-700/60 hover:bg-emerald-500/30"
        }`}
      >
        {isTest ? <Flask size={11} weight="fill" /> : <Lightning size={11} weight="fill" />}
        {isTest ? "Test" : "Live"}
      </button>
    );
  }

  return (
    <div
      role="radiogroup"
      aria-label="Data mode"
      data-testid={testId}
      className="inline-flex items-center gap-0.5 rounded-md border border-zinc-700 bg-zinc-900 p-0.5 h-8 w-full"
    >
      <ModeSegment
        active={viewMode === "live"}
        onClick={() => setViewMode("live")}
        testId={`${testId}-live`}
        label="Live"
        Icon={Lightning}
        activeClass="bg-emerald-500 text-white"
      />
      <ModeSegment
        active={viewMode === "test"}
        onClick={() => setViewMode("test")}
        testId={`${testId}-test`}
        label="Test"
        Icon={Flask}
        activeClass="bg-yellow-400 text-yellow-950"
      />
    </div>
  );
}

function ModeSegment({ active, onClick, testId, label, Icon, activeClass }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      data-testid={testId}
      className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded h-full text-[10px] uppercase tracking-widest font-bold transition-colors ${
        active ? activeClass : "text-zinc-400 hover:text-white hover:bg-zinc-800"
      }`}
    >
      <Icon size={11} weight="bold" />
      {label}
    </button>
  );
}
